/**
 * Swatch System
 * Handles interactive product card swatches:
 * - Hover to change product image
 * - Click to set active swatch
 * - Stores original image for reset
 */

class SwatchSystem {
  constructor() {
    this.init();
  }

  init() {
    // Use event delegation for better performance
    document.addEventListener('mouseenter', this.handleSwatchHover.bind(this), true);
    document.addEventListener('mouseleave', this.handleSwatchLeave.bind(this), true);
    document.addEventListener('click', this.handleSwatchClick.bind(this));

    // Initialize all swatch containers
    this.initSwatchContainers();
  }

  initSwatchContainers() {
    document.querySelectorAll('[data-swatch-container]').forEach(container => {
      this.setupContainer(container);
    });
  }

  setupContainer(container) {
    if (container.dataset.swatchInitialized) return;
    container.dataset.swatchInitialized = 'true';

    const card = container.closest('[data-product-card]');
    if (!card) return;

    const productImage = card.querySelector('[data-product-image]');
    if (productImage) {
      // Store original image
      container.dataset.originalImage = productImage.src;
      container.dataset.originalSrcset = productImage.srcset || '';
    }
  }

  handleSwatchHover(e) {
    // Guard: ensure e.target is an Element (not text node)
    if (!e.target || !e.target.closest) return;

    const swatch = e.target.closest('[data-swatch-value]');
    if (!swatch) return;

    const container = swatch.closest('[data-swatch-container]');
    if (!container || container.dataset.swatchChangeImage !== 'true') return;

    const variantImage = swatch.dataset.variantImage;
    if (!variantImage) return;

    const card = container.closest('[data-product-card]');
    if (!card) return;

    const productImage = card.querySelector('[data-product-image]');
    if (!productImage) return;

    // Store original if not already stored
    if (!container.dataset.originalImage) {
      container.dataset.originalImage = productImage.src;
      container.dataset.originalSrcset = productImage.srcset || '';
    }

    // Change image
    productImage.src = variantImage;
    productImage.srcset = '';
    productImage.style.opacity = '0';

    // Fade in
    requestAnimationFrame(() => {
      productImage.style.transition = 'opacity 0.2s ease';
      productImage.style.opacity = '1';
    });
  }

  handleSwatchLeave(e) {
    // Guard: ensure e.target is an Element (not text node)
    if (!e.target || !e.target.closest) return;

    const swatch = e.target.closest('[data-swatch-value]');
    if (!swatch) return;

    const container = swatch.closest('[data-swatch-container]');
    if (!container || container.dataset.swatchChangeImage !== 'true') return;

    // Check if we're moving to another swatch in the same container
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest('[data-swatch-container]') === container) {
      return; // Don't reset if moving to another swatch
    }

    // Check if there's an active swatch
    const activeSwatch = container.querySelector('.product-card__swatch.is-active');
    if (activeSwatch) {
      // Reset to active swatch's image instead of original
      const activeImage = activeSwatch.dataset.variantImage;
      if (activeImage) {
        this.setProductImage(container, activeImage);
        return;
      }
    }

    // Reset to original image
    this.resetToOriginal(container);
  }

  handleSwatchClick(e) {
    // Guard: ensure e.target is an Element (not text node)
    if (!e.target || !e.target.closest) return;

    const swatch = e.target.closest('[data-swatch-value]');
    if (!swatch) return;

    const container = swatch.closest('[data-swatch-container]');
    if (!container) return;

    // Remove active from all swatches
    container.querySelectorAll('.product-card__swatch').forEach(s => {
      s.classList.remove('is-active');
    });

    // Set clicked swatch as active
    swatch.classList.add('is-active');

    // If swatch has variant image, update stored original
    const variantImage = swatch.dataset.variantImage;
    if (variantImage && container.dataset.swatchChangeImage === 'true') {
      this.setProductImage(container, variantImage);
    }

    // Publish event for other components
    if (window.Storefront && window.Storefront.pubsub) {
      window.Storefront.pubsub.publish('swatch:selected', {
        productId: container.closest('[data-product-card]')?.dataset.productId,
        variantId: swatch.dataset.variantId,
        value: swatch.dataset.swatchValue,
        image: variantImage
      });
    }
  }

  setProductImage(container, imageSrc) {
    const card = container.closest('[data-product-card]');
    if (!card) return;

    const productImage = card.querySelector('[data-product-image]');
    if (!productImage) return;

    productImage.src = imageSrc;
    productImage.srcset = '';
  }

  resetToOriginal(container) {
    const card = container.closest('[data-product-card]');
    if (!card) return;

    const productImage = card.querySelector('[data-product-image]');
    if (!productImage) return;

    const originalImage = container.dataset.originalImage;
    const originalSrcset = container.dataset.originalSrcset;

    if (originalImage) {
      productImage.style.opacity = '0';

      requestAnimationFrame(() => {
        productImage.src = originalImage;
        if (originalSrcset) {
          productImage.srcset = originalSrcset;
        }
        productImage.style.transition = 'opacity 0.2s ease';
        productImage.style.opacity = '1';
      });
    }
  }

  // Public method to reinitialize (for AJAX-loaded content)
  reinit() {
    this.initSwatchContainers();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.swatchSystem = new SwatchSystem();
  });
} else {
  window.swatchSystem = new SwatchSystem();
}

// Reinitialize on Shopify section load (theme editor)
document.addEventListener('shopify:section:load', () => {
  if (window.swatchSystem) {
    window.swatchSystem.reinit();
  }
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SwatchSystem;
}
